import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import useToggle from 'ext/lib/hooks/use-toggle';
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import { Accordion, Icon } from '@appcues/sonar';
import {
  FieldSet,
  Label,
  LightButton,
  ControlledInput as Input,
  Select,
} from 'ext/components/ui';
import { selectUserPreferences } from 'entities/user-preferences/reducer';
import { updatePreview } from 'entities/user-interface/actions';
import { selectPreviewType } from 'entities/user-interface/reducer';
import { StyleShape } from 'entities/step-children/shape';
import { COLOR_MODES } from 'lib/user-preferences';
import { INPUT_COLOR } from 'lib/user-interface';
import ColorInput from 'components/ColorInput';
import InputWithValidation from 'components/InputWithValidation';
import useStyleSettings, {
  DEFAULT_COLORS,
} from 'components/SideBarSettings/Shared/use-style-settings';
import {
  Controls,
  GroupedFieldSet,
  GroupedField,
} from 'components/SideBarSettings/Shared/styled';
import HelpLabel from 'components/SideBarSettings/Shared/HelpLabel';
import useAccordionClick from 'components/SideBarSettings/Shared/use-accordion-click';
import CustomFontButton from 'components/SideBarSettings/Shared/Fonts/CustomFontButton';
import CustomFontModal from 'components/SideBarSettings/Shared/Fonts/CustomFontModal';
import FontGroupOptions from 'components/SideBarSettings/Shared/Fonts/FontGroupOptions';
import {
  getGroupedOptions,
  getGroupedOptionsValues,
  setCustomFontName,
} from 'components/SideBarSettings/Shared/Fonts/font-group-options';

export function TextStyle({
  content,
  onChange,
  selectedPreviewType,
  onPreviewUpdate,
  colorMode,
}) {
  const trackAccordion = useAccordionClick();
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const {
    id: blockId,
    textFieldStyle,
    placeholder: { id: placeholderId, style: placeholderStyle },
  } = content ?? {};

  const blockPreviewType = selectedPreviewType(blockId, INPUT_COLOR);

  const groupedOptions = getGroupedOptions();
  const groupedOptionsValues = getGroupedOptionsValues(groupedOptions);

  const handleChange = useCallback(
    ({ elementId, isPlaceholder } = {}) =>
      patch => {
        onChange({
          blockId: elementId,
          style: patch,
          isPlaceholder,
        });
      },
    [onChange]
  );

  const [valueFor, handleChangeFor] = useStyleSettings({
    onChange: handleChange({}),
    style: textFieldStyle,
    colorMode,
  });

  const [placeholderValueFor, placeholderHandleChangeFor] = useStyleSettings({
    onChange: handleChange({
      elementId: placeholderId,
      isPlaceholder: true,
    }),
    style: placeholderStyle,
    colorMode,
  });

  const handleSelectedFontValue = groupedOptionsValues.find(
    ({ value }) => value === valueFor.fontName
  );

  // If there's a custom font for the text that's not currently in the options (ie in localStorage), add it
  if (handleSelectedFontValue === undefined) {
    setCustomFontName(valueFor.fontName);
  }

  const handleCustomFontButtonClick = () => {
    toggleIsModalOpen();
    setIsMenuOpen(false);
  };

  const handleAddFont = value => {
    handleChangeFor.fontName({ value });
    setCustomFontName(value);
  };

  return (
    <Accordion.Item value="text-style">
      <Accordion.Header>
        <Accordion.Trigger
          onClick={() => trackAccordion('TextField', 'Text style')}
        >
          Text style
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content>
        <Controls>
          <FieldSet>
            <Label htmlFor="text-font">Font</Label>
            <Select
              id="text-font"
              options={groupedOptions}
              formatGroupLabel={FontGroupOptions}
              placeholder="Select font"
              value={handleSelectedFontValue}
              onChange={handleChangeFor.fontName}
              portal
              menuIsOpen={isMenuOpen}
              onMenuOpen={() => setIsMenuOpen(true)}
              onMenuClose={() => setIsMenuOpen(false)}
              menuButton={
                <CustomFontButton onClick={handleCustomFontButtonClick} />
              }
            />
            <CustomFontModal
              onAddFont={handleAddFont}
              onClose={toggleIsModalOpen}
              visible={isModalOpen}
            />
          </FieldSet>
          <GroupedFieldSet>
            <GroupedField half>
              <Label htmlFor="text-font-size">Font size</Label>
              <InputWithValidation
                id="text-font-size"
                value={valueFor.fontSize}
                onChange={handleChangeFor.fontSize}
                type="number"
                min="0"
              />
            </GroupedField>

            <GroupedField half>
              <Label htmlFor="text-line-height">Line height</Label>
              <Input
                id="text-line-height"
                defaultValue={valueFor.lineHeight}
                onChange={handleChangeFor.lineHeight}
                type="number"
                placeholder="Auto"
                min="0"
              />
            </GroupedField>
          </GroupedFieldSet>

          <FieldSet>
            <Label htmlFor="text-placeholder-color">Placeholder color</Label>
            <ColorInput
              id="text-placeholder-color"
              color={placeholderValueFor.foregroundColor}
              placeholder={DEFAULT_COLORS.textPlaceholderColor}
              onChange={e =>
                placeholderHandleChangeFor.foregroundColor(
                  e,
                  'textPlaceholderColor'
                )
              }
            />
          </FieldSet>

          <GroupedFieldSet>
            <GroupedField>
              <Label htmlFor="text-color">
                Input color
                <HelpLabel>
                  Option to set the color <br />
                  for the input text
                </HelpLabel>
              </Label>
              <ColorInput
                id="text-color"
                color={valueFor.foregroundColor}
                onChange={handleChangeFor.foregroundColor}
              />
            </GroupedField>

            <GroupedField>
              <LightButton
                aria-label="Preview input text"
                aria-pressed={blockPreviewType.show}
                kind="tertiary"
                onClick={() =>
                  onPreviewUpdate({
                    blockId,
                    previewType: blockPreviewType.type ?? INPUT_COLOR,
                    show: !(blockPreviewType.show ?? false),
                  })
                }
              >
                <Icon icon={faEye} size="small" />
              </LightButton>
            </GroupedField>
          </GroupedFieldSet>
        </Controls>
      </Accordion.Content>
    </Accordion.Item>
  );
}

TextStyle.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string,
    textFieldStyle: StyleShape,
    placeholder: PropTypes.shape({
      id: PropTypes.string,
      style: StyleShape,
    }),
  }),
  onChange: PropTypes.func,
  selectedPreviewType: PropTypes.func,
  onPreviewUpdate: PropTypes.func,
  colorMode: PropTypes.oneOf(COLOR_MODES),
};

const mapStateToProps = state => ({
  selectedPreviewType: (blockId, previewType) =>
    selectPreviewType(state, { blockId, previewType }),
  colorMode: selectUserPreferences(state).colorMode,
});

const mapDispatchToProps = {
  onPreviewUpdate: updatePreview,
};

export default connect(mapStateToProps, mapDispatchToProps)(TextStyle);
